const mNormalizeMoney = (value) => {
  return String(value).replace(/(?:\s|&nbsp;)*kr\.(?=<|$)/gi, 'kr').replace(/(?:\s|&nbsp;)+kr(?=<|$)/gi, 'kr');
};

const mFormatMoneyFromTheme = (cents, currencyCode) => {
  const moneyFormat = window.theme?.moneyFormat;

  if (!moneyFormat) {
    return mNormalizeMoney(new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
    }).format((Number(cents) || 0) / 100));
  }

  const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
    const fixedAmount = amount.toFixed(precision);
    const [dollars, remainder] = fixedAmount.split('.');

    return (
      dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
      (precision > 0 && remainder ? decimal + remainder : '')
    );
  };

  const placeholderValues = {
    amount: formatWithDelimiters((Number(cents) || 0) / 100, 2),
    amount_no_decimals: formatWithDelimiters(Math.round((Number(cents) || 0) / 100), 0),
    amount_with_comma_separator: formatWithDelimiters((Number(cents) || 0) / 100, 2, '.', ','),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(
      Math.round((Number(cents) || 0) / 100),
      0,
      '.',
      ',',
    ),
    amount_with_apostrophe_separator: formatWithDelimiters((Number(cents) || 0) / 100, 2, "'", '.'),
  };

  return mNormalizeMoney(moneyFormat.replace(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/g, (_, placeholder) => {
    return placeholderValues[placeholder] ?? placeholderValues.amount;
  }));
};

if (!customElements.get('m-main-product-media')) {
  customElements.define(
    'm-main-product-media',
    class MMainProductMedia extends HTMLElement {
      constructor() {
        super();
        this.mainSlider = null;
        this.thumbnailSlider = null;
        this.popupSlider = null;
        this.onClick = (event) => this.handleClick(event);
        this.onKeyup = (event) => this.handleKeyup(event);
      }

      connectedCallback() {
        this.mainSliderEl = this.querySelector('[data-m-product-media-main]');
        this.thumbnailSliderEl = this.querySelector('[data-m-product-media-thumbs]');
        this.popupEl = this.querySelector('[data-m-product-media-popup]');
        this.popupSliderEl = this.querySelector('[data-m-product-media-popup-slider]');
        this.paginationEl = this.querySelector('.m-product-media__pagination');
        this.popupPaginationEl = this.querySelector('.m-product-media__popup-pagination');
        this.prevEl = this.querySelector('[data-m-product-media-prev]');
        this.nextEl = this.querySelector('[data-m-product-media-next]');
        this.popupPrevEl = this.querySelector('[data-m-product-media-popup-prev]');
        this.popupNextEl = this.querySelector('[data-m-product-media-popup-next]');

        if (!this.mainSliderEl) return;

        this.addEventListener('click', this.onClick);
        document.addEventListener('keyup', this.onKeyup);
        this.initSliders();
      }

      initSliders() {
        if (this.mainSlider) return;

        if (!window.Swiper) {
          window.setTimeout(() => this.initSliders(), 100);
          return;
        }

        if (this.thumbnailSliderEl) {
          this.thumbnailSlider = new Swiper(this.thumbnailSliderEl, {
            slidesPerView: 5,
            spaceBetween: 16,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
          });
        }

        this.mainSlider = new Swiper(this.mainSliderEl, {
          slidesPerView: 1,
          speed: 450,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          navigation:
            this.prevEl && this.nextEl
              ? {
                  prevEl: this.prevEl,
                  nextEl: this.nextEl,
                }
              : undefined,
          pagination: this.paginationEl
            ? {
                el: this.paginationEl,
                clickable: true,
              }
            : undefined,
          thumbs: this.thumbnailSlider
            ? {
                swiper: this.thumbnailSlider,
              }
            : undefined,
        });

        if (this.popupSliderEl) {
          this.popupSlider = new Swiper(this.popupSliderEl, {
            slidesPerView: 1,
            speed: 450,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            navigation:
              this.popupPrevEl && this.popupNextEl
                ? {
                    prevEl: this.popupPrevEl,
                    nextEl: this.popupNextEl,
                  }
                : undefined,
            pagination: this.popupPaginationEl
              ? {
                  el: this.popupPaginationEl,
                  type: 'fraction',
                }
              : undefined,
          });
        }

        this.updateSliders();
      }

      updateSliders() {
        window.requestAnimationFrame(() => {
          this.thumbnailSlider?.update();
          this.mainSlider?.update();
          this.popupSlider?.update();
        });
      }

      handleClick(event) {
        const openButton = event.target.closest('[data-m-product-media-open]');
        const closeButton = event.target.closest('[data-m-product-media-close]');

        if (openButton) {
          this.openPopup(Number(openButton.dataset.mProductMediaOpen || 0));
          return;
        }

        if (closeButton) {
          this.closePopup();
        }
      }

      handleKeyup(event) {
        if (event.key !== 'Escape') return;

        this.closePopup();
      }

      openPopup(index = 0) {
        if (!this.popupEl) return;

        this.lockScroll();
        this.popupEl.classList.add('is-open');
        this.popupEl.setAttribute('aria-hidden', 'false');
        this.popupSlider?.slideTo(index, 0);
        this.updateSliders();
      }

      closePopup() {
        if (!this.popupEl?.classList.contains('is-open')) return;

        this.popupEl.classList.remove('is-open');
        this.popupEl.setAttribute('aria-hidden', 'true');
        this.unlockScroll();
      }

      lockScroll() {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.setProperty('overflow', 'hidden', 'important');
        document.body.style.setProperty('padding-right', `${scrollbarWidth}px`, 'important');
      }

      unlockScroll() {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('keyup', this.onKeyup);

        this.mainSlider?.destroy(true, true);
        this.thumbnailSlider?.destroy(true, true);
        this.popupSlider?.destroy(true, true);

        this.mainSlider = null;
        this.thumbnailSlider = null;
        this.popupSlider = null;
      }
    },
  );
}

(() => {
  const styleId = 'm-klarna-container-reset';
  const styleContent = `
    .container {
      padding: 0 !important;
      margin: 4px 0 0 0 !important;
      border: 0 !important;
      flex-direction: row-reverse !important;
      justify-content: flex-end !important;
    }

    .text-wrapper {
        margin-left: 0px !important;
        margin-right: 8px !important;
    }

    svg {
      height: 24px !important;
      width: 56px !important;
    }
  `;

  function applyKlarnaContainerStyles() {
    const placements = document.querySelectorAll('.m-main-product__app klarna-placement');

    placements.forEach((placement) => {
      placement.style.setProperty('padding', '0', 'important');
      placement.style.setProperty('margin', '4px 0 0 0', 'important');
      placement.style.setProperty('border', '0', 'important');

      const shadowRoot = placement.shadowRoot;
      if (!shadowRoot || shadowRoot.getElementById(styleId)) return;

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = styleContent;
      shadowRoot.appendChild(style);
    });
  }

  function initKlarnaContainerStyles() {
    applyKlarnaContainerStyles();

    const observer = new MutationObserver(applyKlarnaContainerStyles);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    window.setTimeout(applyKlarnaContainerStyles, 500);
    window.setTimeout(applyKlarnaContainerStyles, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKlarnaContainerStyles, { once: true });
  } else {
    initKlarnaContainerStyles();
  }
})();

if (!customElements.get('m-main-product-variant-picker')) {
  customElements.define(
    'm-main-product-variant-picker',
    class MMainProductVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.productVariants = [];
        this.selectedOptions = [];
        this.currentVariant = null;
        this.onClick = (event) => this.handleClick(event);
        this.onDocumentClick = (event) => this.handleDocumentClick(event);
        this.onKeyup = (event) => this.handleKeyup(event);
      }

      connectedCallback() {
        this.variantJsonEl = this.querySelector('[data-product-variants-json]');
        this.optionEls = Array.from(this.querySelectorAll('[data-product-option]'));
        this.buttonEls = Array.from(this.querySelectorAll('[data-option-value]'));
        this.variantIdInput = this.querySelector('[data-product-selected-variant-id]');
        this.sectionEl = this.closest('#main-product');
        this.priceEl = this.sectionEl?.querySelector('[data-product-price]');
        this.drawerOverlay = document.querySelector('[data-drawer-overlay]');
        this.activeSizeGuideDrawer = null;
        this.currencyCode = this.dataset.currencyCode || 'USD';
        this.productUrl = this.dataset.productUrl || window.location.pathname;
        this.saleLabel = this.dataset.saleLabel || 'Sale';
        this.fastDeliveryLabel = this.dataset.fastDeliveryLabel || 'Fast delivery';
        this.notFastDeliveryLabel = this.dataset.notFastDeliveryLabel || 'Not fast delivery';
        this.fastDeliveryIcon = this.querySelector('.m-main-product__variant-dropdown-item-delivery.is-fast svg')?.outerHTML || '';
        this.optionInventoryLimit = Number(this.dataset.optionInventoryLimit || 0);
        this.optionInventoryExactLimit = Number(this.dataset.optionInventoryExactLimit || 0);
        this.optionInventoryTemplate = this.dataset.optionInventoryTemplate || '';
        this.optionInventoryFewTemplate = this.dataset.optionInventoryFewTemplate || '';

        this.productVariants = this.getProductVariants();
        this.selectedOptions = this.getInitialSelectedOptions();
        this.currentVariant = this.getSelectedVariant();

        this.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keyup', this.onKeyup);
        this.render();
      }

      getProductVariants() {
        try {
          return JSON.parse(this.variantJsonEl?.textContent || '[]');
        } catch (error) {
          console.error('MMainProductVariantPicker: variant JSON error', error);
          return [];
        }
      }

      getInitialSelectedOptions() {
        const selectedVariantId = Number(this.dataset.selectedVariantId);
        const selectedVariant = this.productVariants.find((variant) => variant.id === selectedVariantId);

        if (selectedVariant) return Array.from(selectedVariant.options || []);
        if (this.productVariants.length === 1) return Array.from(this.productVariants[0].options || []);

        return this.optionEls.map(() => null);
      }

      handleClick(event) {
        const sizeGuideButton = event.target.closest('[data-size-guide]');
        if (sizeGuideButton) {
          this.closeDropdowns();
          this.openSizeGuide(sizeGuideButton.dataset.sizeGuide);
          return;
        }

        const sizeGuideClose = event.target.closest('[data-size-guide-close]');
        if (sizeGuideClose) {
          this.closeSizeGuide();
          return;
        }

        const toggle = event.target.closest('[data-product-option-toggle]');
        if (toggle) {
          this.toggleDropdown(toggle.closest('[data-product-option-dropdown]'));
          return;
        }

        const button = event.target.closest('[data-option-value]');
        if (!button || button.disabled) return;

        const optionEl = button.closest('[data-product-option]');
        if (!optionEl) return;

        const optionIndex = Number(optionEl.dataset.optionPosition) - 1;
        this.selectedOptions[optionIndex] = button.dataset.optionValue;
        this.currentVariant = this.getSelectedVariant();

        this.render();
        this.updateUrl();
        this.updateVariantInput();
        this.updatePrice();
        this.dispatchVariantChange();
        this.closeDropdown(optionEl);
      }

      handleDocumentClick(event) {
        if (this.activeSizeGuideDrawer && event.target === this.drawerOverlay) {
          this.closeSizeGuide();
          return;
        }

        if (this.contains(event.target)) return;

        this.closeDropdowns();
      }

      handleKeyup(event) {
        if (event.key !== 'Escape') return;

        if (this.activeSizeGuideDrawer) {
          this.closeSizeGuide();
          return;
        }

        this.closeDropdowns();
      }

      getSelectedVariant() {
        if (this.selectedOptions.some((option) => !option)) return null;

        return (
          this.productVariants.find((variant) => {
            return this.selectedOptions.every((option, index) => variant.options?.[index] === option);
          }) || null
        );
      }

      render() {
        this.optionEls.forEach((optionEl, optionIndex) => {
          const selectedValueEl = optionEl.querySelector('[data-option-selected-value]');
          const selectedValue = this.selectedOptions[optionIndex] || '';

          if (selectedValueEl) selectedValueEl.textContent = selectedValue;

          const toggleTextEl = optionEl.querySelector('[data-option-toggle-text]');
          if (toggleTextEl && selectedValue) {
            toggleTextEl.textContent = selectedValue;
          }

          this.renderToggleDelivery(optionEl, optionIndex, selectedValue);
          this.renderToggleInventory(optionEl, optionIndex, selectedValue);

          optionEl.querySelectorAll('[data-option-value]').forEach((button) => {
            const value = button.dataset.optionValue;
            const isActive = selectedValue === value;
            const isAvailable = this.isValueAvailable(optionIndex, value);

            button.classList.toggle('is-active', isActive);
            button.classList.toggle('is-disabled', !isAvailable);
            button.disabled = !isAvailable;
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        });
      }

      renderToggleDelivery(optionEl, optionIndex, selectedValue) {
        const deliveryEl = optionEl.querySelector('[data-option-toggle-delivery]');
        if (!deliveryEl) return;

        deliveryEl.hidden = !selectedValue;
        if (!selectedValue) return;

        const stock = this.getOptionStock(optionIndex, selectedValue);

        deliveryEl.classList.toggle('is-fast', stock.inStock);
        deliveryEl.dataset.totalQty = String(stock.totalQty);

        if (stock.inStock) {
          deliveryEl.innerHTML = `${this.fastDeliveryIcon}${this.fastDeliveryLabel}`;
          return;
        }

        deliveryEl.textContent = this.notFastDeliveryLabel;
      }

      renderToggleInventory(optionEl, optionIndex, selectedValue) {
        const inventoryEl = optionEl.querySelector('[data-option-toggle-inventory]');
        if (!inventoryEl) return;

        const inventoryQuantity = Number(this.currentVariant?.inventory_quantity || 0);
        const isCurrentOption = this.currentVariant?.options?.[optionIndex] === selectedValue;
        const inventoryText = this.getToggleInventoryText(inventoryQuantity);
        const isVisible = Boolean(selectedValue && isCurrentOption && inventoryText);

        inventoryEl.hidden = !isVisible;
        if (!isVisible) return;

        inventoryEl.textContent = inventoryText;
      }

      getToggleInventoryText(inventoryQuantity) {
        if (inventoryQuantity < 1 || inventoryQuantity > this.optionInventoryLimit) return '';

        if (inventoryQuantity <= this.optionInventoryExactLimit) {
          return this.optionInventoryTemplate.replace('[[inventory]]', inventoryQuantity);
        }

        return this.optionInventoryFewTemplate;
      }

      toggleDropdown(optionEl) {
        if (!optionEl) return;

        const isOpen = optionEl.classList.contains('is-open');
        this.closeDropdowns();

        if (!isOpen) this.openDropdown(optionEl);
      }

      openDropdown(optionEl) {
        const toggle = optionEl.querySelector('[data-product-option-toggle]');

        optionEl.classList.add('is-open');
        toggle?.setAttribute('aria-expanded', 'true');
      }

      closeDropdown(optionEl) {
        const toggle = optionEl.querySelector('[data-product-option-toggle]');

        optionEl.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
      }

      closeDropdowns() {
        this.querySelectorAll('[data-product-option-dropdown].is-open').forEach((optionEl) => {
          this.closeDropdown(optionEl);
        });
      }

      openSizeGuide(type) {
        const drawer = this.querySelector(`[data-size-guide-drawer="${type}"]`);
        if (!drawer) return;

        this.closeSizeGuide();
        this.activeSizeGuideDrawer = drawer;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        this.drawerOverlay?.classList.add('show-overlay');
        this.lockScroll();
      }

      closeSizeGuide() {
        if (!this.activeSizeGuideDrawer) return;

        this.activeSizeGuideDrawer.classList.remove('is-open');
        this.activeSizeGuideDrawer.setAttribute('aria-hidden', 'true');
        this.activeSizeGuideDrawer = null;
        this.drawerOverlay?.classList.remove('show-overlay');
        this.unlockScroll();
      }

      lockScroll() {
        document.body.classList.add('m-size-guide-open');
      }

      unlockScroll() {
        document.body.classList.remove('m-size-guide-open');
      }

      isValueAvailable(optionIndex, value) {
        return this.productVariants.some((variant) => {
          if (!variant.available || variant.options?.[optionIndex] !== value) return false;

          return this.selectedOptions.every((selectedOption, selectedIndex) => {
            if (selectedIndex === optionIndex || !selectedOption) return true;
            return variant.options?.[selectedIndex] === selectedOption;
          });
        });
      }

      getOptionStock(optionIndex, value) {
        return this.productVariants.reduce(
          (stock, variant) => {
            if (variant.options?.[optionIndex] !== value) return stock;

            const inventoryQuantity = Number(variant.inventory_quantity || 0);
            if (inventoryQuantity > 0) {
              stock.totalQty += inventoryQuantity;
              stock.inStock = true;
            }

            return stock;
          },
          {
            inStock: false,
            totalQty: 0,
          },
        );
      }

      updateUrl() {
        if (!this.currentVariant?.id) return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState({}, '', url.toString());
      }

      updateVariantInput() {
        if (!this.variantIdInput) return;

        this.variantIdInput.value = this.currentVariant?.id || '';
      }

      updatePrice() {
        if (!this.priceEl || !this.currentVariant) return;

        const hasComparePrice = this.currentVariant.compare_at_price > this.currentVariant.price;
        const price = this.formatMoney(this.currentVariant.price);
        const comparePrice = this.formatMoney(this.currentVariant.compare_at_price);
        const discount = hasComparePrice
          ? this.formatMoney(this.currentVariant.compare_at_price - this.currentVariant.price)
          : '';
        const productId = this.currentVariant.product_id || '';

        this.priceEl.className = 'm-main-product__price';
        this.priceEl.innerHTML = `
          <span class="m-main-product__price-current${hasComparePrice ? ' m-main-product__price-current--sale' : ''}" data-sl-attribute-p="${productId}" data-product-price-current>${price}</span>
          ${
            hasComparePrice
              ? `<s class="m-main-product__price-old" data-sl-attribute-cap="${productId}" data-product-price-compare>${comparePrice}</s>
                <span class="m-main-product__price-sale" data-product-price-sale>${this.saleLabel} <span data-sl-attribute-discount="${productId}" data-product-price-discount>${discount}</span></span>`
              : ''
          }
        `;
      }

      formatMoney(cents) {
        return mFormatMoneyFromTheme(cents, this.currencyCode);
      }

      dispatchVariantChange() {
        this.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              variant: this.currentVariant,
              options: this.selectedOptions,
            },
          }),
        );
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keyup', this.onKeyup);
        this.closeSizeGuide();
      }
    },
  );
}

if (!customElements.get('m-main-product-klarna')) {
  customElements.define(
    'm-main-product-klarna',
    class MMainProductKlarna extends HTMLElement {
      constructor() {
        super();
        this.onVariantChange = (event) => this.handleVariantChange(event);
      }

      connectedCallback() {
        this.textEl = this.querySelector('[data-klarna-text]');
        this.sectionEl = this.closest('#main-product');
        this.currencyCode = this.dataset.currencyCode || window.Shopify?.currency?.active || 'DKK';
        this.installments = Math.max(1, Number(this.dataset.installments || 3));
        this.minPriceForAmount = Number(this.dataset.minPriceForAmount || 0);
        this.template = this.dataset.template || '[[installments]] betalinger af [[installment_price]] til 0% i rente med';
        this.templateNoAmount = this.dataset.templateNoAmount || '[[installments]] betalinger til 0% i rente med';

        this.sectionEl?.addEventListener('variant:change', this.onVariantChange);
        this.render(Number(this.dataset.price || 0));
      }

      handleVariantChange(event) {
        const price = Number(event.detail?.variant?.price || 0);

        if (!price) return;

        this.render(price);
      }

      render(price) {
        if (!this.textEl) return;

        const template = price >= this.minPriceForAmount ? this.template : this.templateNoAmount;
        const installmentPrice = Math.floor(price / this.installments);

        this.textEl.textContent = template
          .replaceAll('[[installments]]', this.installments)
          .replaceAll('[[installment_price]]', this.formatMoney(installmentPrice));
      }

      formatMoney(cents) {
        return mFormatMoneyFromTheme(cents, this.currencyCode);
      }

      disconnectedCallback() {
        this.sectionEl?.removeEventListener('variant:change', this.onVariantChange);
      }
    },
  );
}

if (!customElements.get('m-main-product-inventory')) {
  customElements.define(
    'm-main-product-inventory',
    class MMainProductInventory extends HTMLElement {
      constructor() {
        super();
        this.onVariantChange = (event) => this.handleVariantChange(event);
      }

      connectedCallback() {
        this.textEl = this.querySelector('[data-product-inventory-text]');
        this.sectionEl = this.closest('#main-product');
        this.inventoryLimit = Number(this.dataset.inventoryLimit || 0);
        this.inventoryTemplate = this.dataset.inventoryTemplate || '';

        this.sectionEl?.addEventListener('variant:change', this.onVariantChange);
      }

      handleVariantChange(event) {
        this.render(event.detail?.variant);
      }

      render(variant) {
        const inventoryQuantity = Number(variant?.inventory_quantity || 0);
        const isVisible = inventoryQuantity > 0 && inventoryQuantity <= this.inventoryLimit;

        this.hidden = !isVisible;

        if (!isVisible || !this.textEl) return;

        this.textEl.innerHTML = this.inventoryTemplate.replace('[[inventory]]', `<span data-product-inventory-count>${inventoryQuantity}</span>`);
      }

      disconnectedCallback() {
        this.sectionEl?.removeEventListener('variant:change', this.onVariantChange);
      }
    },
  );
}
